import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight, Mail, RefreshCw } from "lucide-react";
import { acceptInvitation, resendInvitation } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setErrorMsg("No activation token provided in link.");
      return;
    }

    const processActivation = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        await acceptInvitation(token);
        setSuccess(true);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail === "EXPIRED_TOKEN") {
          setErrorMsg("This invitation link has expired. Please request a new activation link.");
        } else if (detail === "ALREADY_USED") {
          setErrorMsg("This invitation link has already been used to activate the organization.");
        } else if (detail === "INVALID_TOKEN") {
          setErrorMsg("Invalid activation token. Please check your invitation email or request a new one.");
        } else {
          setErrorMsg(typeof detail === "string" ? detail : "Failed to activate organization invitation.");
        }
      } finally {
        setLoading(false);
      }
    };

    processActivation();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendLoading(true);
    setResendSuccess(null);
    try {
      await resendInvitation(resendEmail);
      setResendSuccess("A new activation link has been sent to your email address.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setResendSuccess(`Error: ${typeof detail === "string" ? detail : "Failed to resend activation link."}`);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center items-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center mb-4">
          <Logo height={48} />
        </div>

        <div className="rounded-xl border border-border bg-card p-8 shadow-sm text-center">
          {loading ? (
            <div className="space-y-4 py-8">
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
              <h2 className="serif text-xl text-primary font-medium">Validating Invitation</h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we verify your activation token and setup your organization...
              </p>
            </div>
          ) : success ? (
            <div className="space-y-6">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mx-auto flex items-center justify-center">
                <CheckCircle2 size={32} />
              </div>

              <div className="space-y-2">
                <h1 className="serif text-2xl text-primary font-semibold">
                  Organization Activated!
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your organization and administrator account have been successfully verified and activated.
                </p>
              </div>

              <button
                onClick={() => navigate("/login")}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Sign In to Your Organization <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive mx-auto flex items-center justify-center">
                <AlertCircle size={32} />
              </div>

              <div className="space-y-2">
                <h1 className="serif text-xl text-primary font-semibold">
                  Activation Failed
                </h1>
                <p className="text-sm text-muted-foreground">
                  {errorMsg}
                </p>
              </div>

              <div className="border-t border-border pt-6 text-left space-y-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                  <Mail size={14} /> Request New Activation Link
                </h3>
                <form onSubmit={handleResend} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    type="submit"
                    disabled={resendLoading}
                    className="w-full rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 text-xs font-medium py-2.5 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                  >
                    {resendLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Resend Activation Email
                  </button>
                </form>

                {resendSuccess && (
                  <p className="text-xs p-2.5 rounded bg-accent/50 text-foreground border border-border">
                    {resendSuccess}
                  </p>
                )}
              </div>

              <div className="pt-2">
                <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground underline">
                  Back to Sign In
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
